import cookie from "cookie";
import Cookie from "universal-cookie";
import { get } from "lodash";
import dateFormat from "date-fns/format";
import wretch from "wretch";
import { fetchAuthorDetails } from "./api";
import { getSections, getTags, getCommentCount, getReaderType, isPremium } from "./data/story";
import { getParentSection } from "./data/section";
import { STORY_TEMPLATES, PAGE_TYPE } from "./constants";

function getDeviceTrackerId() {
  let cookies = cookie.parse(document.cookie);
  return cookies["thinmint"];
}

function getContentSource(story) {
  return get(story, ["metadata", "story-attributes", "source", 0]);
}

function getNewsType(story) {
  // Short News: SN,
  // Else: N
  return get(story, ["metadata", "story-attributes", "shortnews", 0]) === "true" ? "SN" : "N";
}

function getContentPostedShift(story) {
  // Night Shift: NS,
  // General Shift: GS
  // Add from the story attributes for backward compatibility
  const newsDateFromMeta = get(story, ["metadata", "story-attributes", "newsdate", 0]);
  const createdAt = newsDateFromMeta ? new Date(newsDateFromMeta) : new Date(story["created-at"]);
  return createdAt.getHours() >= 21 || createdAt.getHours() <= 7 ? "NS" : "GS";
}

function getDimension5({ story, section, collection, entity, author }) {
  if (!story && !section && !collection && !entity && !author) {
    return "Unknown";
  }
  if (entity) {
    return `${get(entity, ["entity-type-id"], "Unknown")}-${get(entity, ["id"], "Unknown")}`;
  }

  if (author) {
    return get(author, ["id"], "Unknown");
  }

  const IssueCollection = get(story, ["collections"], []).find(collection =>
    get(collection, ["metadata", "entities", "collectionEntities", "magazine", 0, "id"])
  );

  if (IssueCollection && story["access"] === "subscription") {
    // magazine article
    const magazineEntity = get(IssueCollection, ["metadata", "entities", "collectionEntities", "magazine", 0]);
    return `${magazineEntity.id}-${IssueCollection.id}-${story.id}`;
  }

  if (collection) {
    // magazine landing page
    const magazineEntity = get(collection, ["metadata", "entities", "collectionEntities", "magazine", 0]);
    if (magazineEntity) {
      return `${magazineEntity.id}-${collection.id}`;
    } else {
      return collection["id"];
    }
  }

  section = section || get(story, ["sections", 0]);

  if (section) {
    // article or section pages
    const parentSection = getParentSection(section);
    let sections = [section];
    if (parentSection) {
      sections.unshift(parentSection);
    }

    return sections.map(section => section["name"]).join("-");
  }

  return null;
}

function getDimension4(pageType, { section, story, collection, entity, author }) {
  switch (pageType) {
    case PAGE_TYPE.HOME_PAGE:
      return "home";
    case PAGE_TYPE.SECTION_PAGE:
      return get(section, ["name"], "");
    case PAGE_TYPE.STORY_PAGE:
      return `${get(story, ["sections", 0, "name"], "")} | ${get(
        story,
        ["metadata", "story-attributes", "theme", 0],
        ""
      )} | ${getContentSource(story)} | ${getNewsType(story)} | ${getContentPostedShift(story)}`;
    case PAGE_TYPE.RASIPALAN_PAGE:
      return get(story, ["slug"], "Unknown");
    case PAGE_TYPE.GURUPEYARCHI_STORY_PAGE:
      return get(story, ["slug"], "Unknown");
    case PAGE_TYPE.GURUPEYARCHI_PAGE:
    case PAGE_TYPE.BUNDLE_PAGE:
      return collection.name;
    case PAGE_TYPE.TOPIC_PAGE:
      return `${get(entity, ["name"], "Unknown")} | ${get(entity, ["tamil-name"], "Unknown")}`;
    case PAGE_TYPE.AUTHOR_PAGE:
      return `${get(author, ["metadata", "author_name_en"], "Unknown")} | ${get(author, ["name"], "Unknown")}`;
  }
}

export const GA = {
  inited: false,
  loadScript(cb) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.defer = true;
    const node = document.getElementsByTagName("script")[0];
    global.qtInitialGA = function() {
      global.ga("create", global.qtConfig.integrations.gaKey, "auto");
      cb();
    };
    script.onload = global.qtInitialGA;
    script.src = `https://www.google-analytics.com/analytics.js`;
    node.parentNode.insertBefore(script, node);
    global["GoogleAnalyticsObject"] = "ga";
  },
  registerPageView(pageType, { section, story, collection, entity, author, infiniteTagRequired } = {}) {
    try {
      if (!this.inited) {
        this.loadScript(registerPageViewImpl.bind(this));
        this.inited = true;
        return;
      }
      registerPageViewImpl.call(this);
    } catch (e) {
      console.error(e);
    }

    function triggerPageView(user) {
      global.ga(function(tracker) {
        var clientId = tracker.get("clientId");
        if (clientId === "") {
          clientId = "Unknown";
        }
        global.ga("set", "dimension1", user.userType || "Unknown");
        global.ga("set", "dimension2", user.userId || "Unknown");
        global.ga("set", "dimension3", getDeviceTrackerId() || "Unknown");
        global.ga(
          "set",
          "dimension4",
          getDimension4(pageType, { section, story, collection, entity, author }) || "Unknown"
        );
        global.ga("set", "dimension5", getDimension5({ section, story, collection, entity, author }) || "Unknown");
        global.ga("set", "dimension6", clientId);
        global.ga("set", "dimension8", get(story, ["story-template"], "Unknown")); // dimension7 is used for test purpose
        const isMagazineIssue = !!get(collection, ["metadata", "entities", "collectionEntities", "magazine"], [])
          .length;
        if (isMagazineIssue) {
          global.ga("set", "dimension9", collection["magazineEnName"] || "Unknown");
          const issueDate = new Date(collection["collection-date"] || null);
          const issueDateStringified = dateFormat(issueDate, "D-MMMM-YYYY");
          global.ga("set", "dimension10", issueDateStringified, "Unknown");
        } else {
          global.ga("set", "dimension9", "Unknown");
          global.ga("set", "dimension10", "Unknown");
        }
        if (infiniteTagRequired) {
          global.ga("set", "dimension11", "infinite");
        } else {
          global.ga("set", "dimension11", "Unknown");
        }

        if (global.ccaud) {
          global.ga("set", "dimension12", global.ccaud.Profile.pid);
        }

        let cookies = cookie.parse(document.cookie);
        if (cookies["story_c"]) {
          global.ga("set", "dimension13", cookies["story_c"]);
        }

        if (pageType === PAGE_TYPE.STORY_PAGE) {
          global.ga("set", "dimension14", get(story, ["id"]));
        }
        global.ga("send", "pageview", {
          page: `${window.location.pathname}${window.location.search}`,
          hitCallback: () => {
            if (user && user.userId && global.navigator && global.navigator.sendBeacon) {
              global.navigator.sendBeacon(`/api/vikatan/v1/ping-user/ga/${user.userId}`);
            }
          }
        });
      });
    }
    function registerPageViewImpl() {
      if (!global.ga) {
        return;
      }
      if (global.userPromise) {
        global.userPromise.catch(() => ({})).then(user => {
          triggerPageView(user || {});
        });
      } else {
        triggerPageView({});
      }
    }
  },
  customEvent(action, type, label) {
    global.ga("send", "event", action, type, label);
  }
};

export const ContentInsights = {
  loadScript() {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.defer = true;
    script.onload = () => {
      global._ain.track();
    };
    script.src =
      document.location.protocol === "https:"
        ? "https://d7d3cf2e81d293050033-3dfc0615b0fd7b49143049256703bfce.ssl.cf1.rackcdn.com/stf.js"
        : "http://t.contentinsights.com/stf.js";
    const node = document.getElementsByTagName("script")[0];
    node.parentNode.insertBefore(script, node);
  },
  registerNonStoryPageView(contentInsightsId, pageType, { collection, section }) {
    global.userPromise.catch(() => ({})).then(user => {
      const data = {
        id: contentInsightsId,
        reader_type: getReaderType(user.userType),
        postid: (section && section.id) || collection.id,
        maincontent: `.js-content-insights-${(section && section.id) || collection.id}`,
        title: (section && section.name) || collection.name
      };
      data.url = global.location.href || "Unknown";
      if (pageType === PAGE_TYPE.HOME_PAGE) {
        data.sections = "home";
      } else if (pageType === PAGE_TYPE.SECTION_PAGE) {
        data.url = section && section.url ? section.url : data.url;
        data.sections = (section && section.name) || (collection && collection.name) || "Unknown";
      } else if (collection) {
        if (pageType === PAGE_TYPE.MAGAZINE_PAGE) {
          data.sections = `${collection["magazineEnName"]} - Issue Date - ${dateFormat(
            collection["collection-date"],
            "DD-MMMM-YYYY"
          )}`;
        } else {
          const section = get(collection, ["metadata", "section", "0"]);
          if (section) {
            data.url = section.url;
          }
          data.sections = (section && section.name) || collection.name;
        }
      }
      global._ain = { ...global._ain, ...data };

      if (global._ain.track) {
        global._ain.track();
      } else {
        this.loadScript();
      }
    });
  },
  registerPageView(contentInsightsId, story, metypeConfig) {
    let wordCount;
    try {
      wordCount = document.querySelector(`.js-content-insights-${story.id} .js-story`).textContent.split(/\s+/).length;
    } catch (e) {
      console.error("Error calculating word count for content insight");
    }
    Promise.all([global.userPromise.catch(() => ({})), getCommentCount(metypeConfig), fetchAuthorDetails(story)]).then(
      ([user, commentCount, author]) => {
        const data = {
          id: contentInsightsId,
          url: story.url,
          postid: get(story, ["id"], ""),
          maincontent: `.js-content-insights-${story.id} .js-story`,
          title: get(story, ["headline"], ""),
          pubdate: get(story, ["last-published-at"]) ? new Date(get(story, ["last-published-at"])).toISOString() : "",
          authors: get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name,
          sections: getSections(story),
          tags: getTags(story),
          comments: commentCount,
          reader_type: getReaderType(user.userType),
          access_level: isPremium(story) ? "paid" : "free",
          word_count: wordCount
        };
        global._ain = { ...global._ain, ...data };

        if (global._ain.track) {
          global._ain.track();
        } else {
          this.loadScript();
        }
      }
    );
  }
};

export const Chartbeat = {
  inited: false,
  loadScript({ story, section, collection, pageType, entity, tag }) {
    // eslint-disable-next-line camelcase
    let _sf_async_config = (window._sf_async_config = window._sf_async_config || {});
    function loadChartbeat() {
      var e = document.createElement("script");
      var n = document.getElementsByTagName("script")[0];
      e.type = "text/javascript";
      e.defer = true;
      e.src = "//static.chartbeat.com/js/chartbeat.js";
      n.parentNode.insertBefore(e, n);
    }

    if (story) {
      fetchAuthorDetails(story).then(author => {
        _sf_async_config.uid = 61123;
        _sf_async_config.domain = "vikatan.com";
        _sf_async_config.useCanonical = true;
        _sf_async_config.sections = getSections(story);
        _sf_async_config.authors =
          get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name;
        loadChartbeat();
      });
    } else {
      _sf_async_config.uid = 61123;
      _sf_async_config.domain = "vikatan.com";
      _sf_async_config.useCanonical = true;
      _sf_async_config.path = global.location && global.location.pathname;
      if (pageType === PAGE_TYPE.HOME_PAGE) {
        _sf_async_config.sections = "home";
      } else if (pageType === PAGE_TYPE.SECTION_PAGE) {
        _sf_async_config.sections = (section && section.name) || (collection && collection.name) || "Unknown";
      } else if (pageType === PAGE_TYPE.TOPIC_PAGE) {
        _sf_async_config.sections = `${get(entity, ["name"], "Unknown")} | ${get(entity, ["tamil-name"], "Unknown")}`;
      } else if (pageType === PAGE_TYPE.TAG_PAGE) {
        _sf_async_config.sections = `${get(tag, ["name"], get(tag, ["title"], "Unknown"))}`;
      } else if (collection) {
        if (pageType === PAGE_TYPE.MAGAZINE_PAGE) {
          _sf_async_config.sections = `${collection["magazineEnName"]} - Issue Date - ${dateFormat(
            collection["collection-date"],
            "DD-MMMM-YYYY"
          )}`;
        } else {
          const section = get(collection, ["metadata", "section", "0"]);
          _sf_async_config.sections = (section && section.name) || collection.name;
        }
      }
      loadChartbeat();
    }
  },
  registerNonStoryPageView(pageType, { section, collection, entity, tag } = {}) {
    if (!this.inited) {
      this.loadScript({ section, collection, pageType, entity });
      this.inited = true;
    } else {
      const data = {
        domain: "vikatan.com",
        probability_is_control: 0,
        probability_is_lift: 0,
        host: "vikatan.com",
        experiments: {},
        sections: "Unknown",
        path: global.location && `${global.location.pathname}${global.location.search}`
      };
      if (pageType === PAGE_TYPE.HOME_PAGE) {
        data["path"] = "/";
        data["sections"] = "home";
      } else if (pageType === PAGE_TYPE.SECTION_PAGE) {
        data["sections"] = (section && section.name) || (collection && collection.name) || "Unknown";
      } else if (pageType === PAGE_TYPE.TOPIC_PAGE) {
        data["sections"] = `${get(entity, ["name"], "Unknown")} | ${get(entity, ["tamil-name"], "Unknown")}`;
      } else if (pageType === PAGE_TYPE.TAG_PAGE) {
        data.sections = `${get(tag, ["name"], get(tag, ["title"], "Unknown"))}`;
      } else if (collection) {
        if (pageType === PAGE_TYPE.MAGAZINE_PAGE) {
          data["sections"] = `${collection["magazineEnName"]} - Issue Date - ${dateFormat(
            collection["collection-date"],
            "DD-MMMM-YYYY"
          )}`;
        } else {
          const section = get(collection, ["metadata", "section", "0"]);
          data["sections"] = (section && section.name) || collection.name;
        }
      }
      global.pSUPERFLY && global.pSUPERFLY.virtualPage(data);
    }
  },
  registerPageView(story) {
    try {
      if (!this.inited) {
        this.loadScript({ story });
        this.inited = true;
      } else {
        fetchAuthorDetails(story).then(author => {
          const data = {
            sections: getSections(story),
            authors:
              get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name,
            path: global.location && global.location.pathname,
            title: get(story, ["headline"], "")
          };

          global.pSUPERFLY && global.pSUPERFLY.virtualPage(data);
        });
      }
    } catch (e) {
      console.error(e);
    }
  }
};
export const ComScore = {
  inited: false,
  loadScript() {
    global._comscore = global._comscore || [];
    global._comscore.push({ c1: "2", c2: "20272655" });
    (function() {
      var s = document.createElement("script");
      var el = document.getElementsByTagName("script")[0];
      s.defer = true;
      s.src =
        (document.location.protocol === "https:" ? "https://sb" : "http://b") + ".scorecardresearch.com/beacon.js";
      el.parentNode.insertBefore(s, el);
    })();
  },
  registerPageView() {
    try {
      if (!this.inited) {
        this.loadScript();
        this.inited = true;
      } else {
        global.COMSCORE && global.COMSCORE.beacon({ c1: "2", c2: "20272655" });
        wretch("/v/api/comscore")
          .headers({ "Content-Type": "application/json" })
          .get();
      }
    } catch (e) {
      console.error(e);
    }
  }
};

/* Needed for WebEngage and DeepBi */

const loadCcaud = {
  inited: false,
  init() {
    if (this.inited) {
      return;
    }
    this.inited = true;
    var e = document.createElement("script");
    var n = document.getElementsByTagName("script")[0];
    e.type = "text/javascript";
    e.defer = true;
    e.src = "https://ad.crwdcntrl.net/5/c=9592/pe=y/var=ccaud";
    n.parentNode.insertBefore(e, n);
  }
};

export const DeepBI = {
  inited: false,
  paywallStory(story, issueCollection) {
    try {
      let gaInitializerListenerId = setInterval(function() {
        if (global.ga) {
          clearInterval(gaInitializerListenerId);
          paywallGAEvent();
        }
      });

      let deepInitializerListenerId = setInterval(function() {
        if (global.deep) {
          clearInterval(deepInitializerListenerId);
          paywallEvents();
        }
      });

      paywallWE();
    } catch (e) {
      console.error(e);
    }

    function paywallEvents() {
      global.deep.event({
        "event.type": "paywall-displayed",
        "paywall type": "premium"
      });
    }

    function paywallGAEvent() {
      global.ga("send", "event", "paywall-displayed", "premium", "P");
    }

    function paywallWE() {
      if (global.webengage) {
        global.userPromise.catch(() => null).then(user => {
          const issueDate = new Date(issueCollection["collection-date"] || null);
          const issueDateStringified = dateFormat(issueDate, "D-MMMM-YYYY");
          const sections = [get(story, ["sections", 0], {})];
          const parentSection = getParentSection(sections[0]);
          if (parentSection) {
            sections.unshift(parentSection);
          }
          let paywallobj = {
            PaywallType: "premium",
            ArticleTitle: story.headline,
            ArticleID: story.id,
            Category: get(sections, [0, "name"]) || "",
            SubCategory: get(sections, [1, "name"]) || "",
            ArticleType: story.access === "subscription" ? "Paid" : "Free",
            Magazine: issueCollection["magazineEnName"] || "",
            IssueDate: issueDateStringified || ""
          };
          global.webengage.track("Paywall Displayed", paywallobj);
          let cookiesnew = new Cookie();
          cookiesnew.set("paywallStory", JSON.stringify(paywallobj), { domain: ".vikatan.com", path: "/" });
        });
      }
    }
  },
  WebengageUserId() {
    let WeInitializerListenerId = setInterval(function() {
      if (typeof global.webengage.user.getAnonymousId === "function") {
        clearInterval(WeInitializerListenerId);
        global.deep &&
          global.deep({
            anonymous_id: global.webengage.user.getAnonymousId()
          });
      }
    });
  },
  setCookie() {
    global.deep &&
      global.deep({
        vikatan: {
          cookie: getDeviceTrackerId()
        }
      });
  },
  setMemberData() {
    global.userPromise
      .then(user => {
        global.deep &&
          global.deep({
            vikatan: {
              customer: {
                ...user,
                loggedIn: true
              }
            }
          });
      })
      .catch(() => {
        global.deep &&
          global.deep({
            vikatan: {
              customer: {
                loggedin: false
              }
            }
          });
      });
  },
  setInitialData() {
    this.setCookie();
    this.setMemberData();
    this.WebengageUserId();
  },
  loadScript(f) {
    /* Deep Tracker (c) 2019-01-28 v3.0.6 */
    let e = window.document.createElement("script");
    e.type = "text/javascript";
    e.charset = "utf-8";
    e.setAttribute("crossorigin", "anonymous");
    e.defer = true;
    e.src = (window.document.location.protocol || "http:") + "//api.deep.bi/v3/sizzle";
    let t = window.document.getElementsByTagName("head")[0];
    t.appendChild(e);
    e = window.document.createElement("script");
    e.type = "text/javascript";
    e.charset = "utf-8";
    e.setAttribute("crossorigin", "anonymous");
    e.defer = true;
    e.src = (window.document.location.protocol || "http:") + "//api.deep.bi/v3/init";
    t.appendChild(e);
    let deepInitializerListenerId = setInterval(
      function() {
        if (window.hasOwnProperty("deepTracker") && window.deepTracker && window.deepTracker.isInitialized()) {
          clearInterval(deepInitializerListenerId);
          window.deep = window.deepTracker.track("17cZaviwFrV2", "Da16NqKwj2619hxwdhdGH9u1");
          window.deep.options.trackconsent = true;
          window.deep.options.dntignore = true;
          window.deep.options.media = true;
          this.setInitialData();
          f && f();
          window.deep();
          this.inited = true;
        }
      }.bind(this)
    );
  },
  registerPageView({ story, section }) {
    try {
      if (!this.inited) {
        this.loadScript(registerPageViewImpl.bind(this));
        return;
      } else {
        loadCcaud.init();
        let lotamePersona = "";
        if (global.ccaud && global.ccaud.Profile.Audiences.Audience.length > 0) {
          let ccaudWeCat = [];
          for (let ccaudForId = 0; ccaudForId < global.ccaud.Profile.Audiences.Audience.length; ccaudForId++) {
            if (global.ccaud.Profile.Audiences.Audience[ccaudForId].abbr.toLowerCase().includes("aud")) {
              ccaudWeCat.push(global.ccaud.Profile.Audiences.Audience[ccaudForId].abbr);
            }
          }
          lotamePersona = String(ccaudWeCat);
        }

        let cookies = cookie.parse(document.cookie);
        global.deep.event({
          "event.type": "page-open",
          "page.path": global.location.href,
          "page.title": global.document.title,
          "page.persona": lotamePersona || "",
          "page.variant": cookies["story_c"] ? cookies["story_c"] : ""
        });
      }
      registerPageViewImpl();
    } catch (e) {
      console.error(e);
    }

    function registerPageViewImpl() {
      if (story) {
        let cookies = cookie.parse(document.cookie);
        const sections = [get(story, ["sections", 0], {})];
        const parentSection = getParentSection(sections[0]);
        if (parentSection) {
          sections.unshift(parentSection);
        }
        fetchAuthorDetails(story).then(author => {
          let data = {
            article: {
              template: story["story-template"],
              title: story.headline,
              slug: story.slug,
              subtitle: story["sub-headline"],
              webtitle: document.title,
              id: story.id,
              publication: {
                date: dateFormat(story["published-at"], "YYYY-MM-DD"),
                ts: story["published-at"]
              },
              ArticleType: story.access === "subscription" ? "Paid" : "Free",
              tags: story.tags.map(tag => tag.name),
              variant: cookies["story_c"] ? cookies["story_c"] : ""
            },
            categories: sections.map(section => section["name"]),
            author: {
              name: [
                get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name
              ]
            }
          };

          const issueCollection = get(story, ["collections"], []).find(collection =>
            get(collection, ["metadata", "entities", "collectionEntities", "magazine", 0, "id"])
          );
          if (issueCollection) {
            data = {
              ...data,
              issue: {
                id: issueCollection.id,
                publication: {
                  date: dateFormat(issueCollection["collection-date"], "YYYY-MM-DD"),
                  ts: issueCollection["collection-date"]
                }
              }
            };
          }
          global.deep && global.deep({ vikatan: data });
          global.deep.addElements([
            { type: "main-container", selector: ".js-story-main-container" },
            { type: "article", selector: ".js-story" },
            { type: "related", selector: ".js-related-stories" },
            { type: "article-header", selector: ".js-story-header" },
            { type: "article-footer", selector: ".js-story-footer" }
          ]);
          global.deep.options.scrolldepth = {
            elements: [{ type: "main-container" }, { type: "article" }]
          };
          global.deep.options.viewability = {
            elements: [{ type: "related" }, { type: "article-header" }, { type: "article-footer" }]
          };
        });
      }
      if (section) {
        let sections = [section];
        const parentSection = getParentSection(section);
        if (parentSection) {
          sections.unshift(parentSection);
        }

        global.deep &&
          global.deep({
            vikatan: {
              categories: sections.map(section => section["name"])
            }
          });
      }
    }
  },
  customEvent(type, label) {
    global.deep.event({
      "event.type": "article-share",
      "social.action": "share",
      "story.id": label,
      "social.click": type
    });
  }
};

export const WebEngage = {
  userRegistered: false,
  inited: false,
  loadScript() {
    function loadWebEngageScript() {
      var e = document.createElement("script");
      var n = document.getElementsByTagName("script")[0];
      e.type = "text/javascript";
      e.defer = true;
      e.id = "_webengage_script_tag";
      e.innerHTML = `var webengage;!function(e,t,n,i,o){function s(e,t){e[t[t.length-1]]=function(){a.__queue.push([t.join("."),arguments])}}var r,g,a=e[n],c=" ",l="init options track screen onReady".split(c),p="feedback survey notification".split(c),u="options render clear abort".split(c),f="Open Close Submit Complete View Click".split(c),w="identify login logout setAttribute".split(c);if(!a||!a.__v){for(e[n]=a={__queue:[],__v:"6.0",user:{}},r=0;r<l.length;r++)s(a,[l[r]]);for(r=0;r<p.length;r++){for(a[p[r]]={},g=0;g<u.length;g++)s(a[p[r]],[p[r],u[g]]);for(g=0;g<f.length;g++)s(a[p[r]],[p[r],"on"+f[g]])}for(r=0;r<w.length;r++)s(a.user,["user",w[r]]);setTimeout(function(){var e=t.createElement("script"),n=t.getElementById("_webengage_script_tag");e.type="text/javascript",e.onload=window.webengageRegisterPageView,e.defer=!0,e.src=("https:"==t.location.protocol?"https://ssl.widgets.webengage.com":"http://cdn.widgets.webengage.com")+"/js/webengage-min-v-6.0.js",n.parentNode.insertBefore(e,n)})}}(window,document,"webengage"),webengage.init('${
        global.qtConfig.integrations.webEngageKey
      }');`;
      n.parentNode.insertBefore(e, n);
    }

    loadWebEngageScript();
    loadCcaud.init();
    this.inited = true;
  },
  registerUser() {
    if (!global.webengage || this.userRegistered) {
      return;
    }
    global.userPromise
      .then(user => {
        const { userId, username, userEmail, userMobileNumber } = user;
        global.webengage.user.setAttribute("Cookie_Id", getDeviceTrackerId());
        // If User logged In pass below attributes
        global.webengage.user.setAttribute("User_Id", userId);
        global.webengage.user.login(userId);
        global.webengage.user.setAttribute("we_first_name", username);
        global.webengage.user.setAttribute("we_email", userEmail);
        userMobileNumber && global.webengage.user.setAttribute("we_phone", userMobileNumber);
      })
      .catch(() => {
        console.warn("User attribution error occured");
      });
    this.userRegistered = true;
  },
  registerPageView({ story, section } = {}) {
    try {
      if (!this.inited) {
        window.webengageRegisterPageView = registerPageViewImpl.bind(this);
        this.loadScript(registerPageViewImpl.bind(this));
        return;
      }

      registerPageViewImpl.call(this);
    } catch (e) {
      console.error(e);
    }

    function registerPageViewImpl() {
      if (!global.webengage) {
        return;
      }
      this.registerUser();

      if (global.ccaud && global.ccaud.Profile.Audiences.Audience.length > 0) {
        let ccaudWeCat = [];
        for (let ccaudForId = 0; ccaudForId < global.ccaud.Profile.Audiences.Audience.length; ccaudForId++) {
          ccaudWeCat.push(global.ccaud.Profile.Audiences.Audience[ccaudForId].id);
        }
        global.webengage.user.setAttribute("action", String(ccaudWeCat));
      }

      if (
        window.hasOwnProperty("deepTracker") &&
        window.deepTracker &&
        window.deepTracker.isInitialized() &&
        typeof window.deepTracker.getScore === "function"
      ) {
        window.deepTracker.getScore({ force: true, type: "profile" }).then(
          score =>
            score &&
            Object.keys(score).length !== 0 &&
            global.webengage.user.setAttribute({
              dbi_rfv_segment: score.rfv_segment,
              dbi_rfv_score: score.rfv_score,
              dbi_rfv_level: score.rfv_level,
              dbi_recency_score: score.recency_score,
              dbi_recency_level: score.recency_level,
              dbi_volume_level: score.volume_level,
              dbi_volume_score: score.volume_score,
              dbi_frequency_level: score.frequency_level,
              dbi_frequency_score: score.frequency_score
            })
        );
      }

      global.webengage.track("Page View", {
        PageTitle: global.document.title,
        PagePath: global.location.href
      });

      let cookies = cookie.parse(document.cookie);
      if (cookies["vusr_unique_views"] !== "1") {
        let d = new Date();
        d.setUTCHours(23, 59, 59, 999);
        let cookiesnew = new Cookie();
        cookiesnew.set("vusr_unique_views", "1", { path: "/", expires: d, domain: ".vikatan.com" });
        global.webengage.track("Unique Visit", {
          PageTitle: global.document.title,
          PagePath: global.location.href
        });
      }

      let type = "Article View";
      if (story) {
        if (story["story-template"] === STORY_TEMPLATES.VIDEO) {
          type = "Video";
        }
        if (story["story-template"] === STORY_TEMPLATES.PHOTO_ALBUM) {
          type = "Album";
        }

        const sections = [get(story, ["sections", 0], {})];
        const parentSection = getParentSection(sections[0]);
        if (parentSection) {
          sections.unshift(parentSection);
        }

        fetchAuthorDetails(story).then(author => {
          global.webengage.track(type, {
            ArticleTitle: story.headline,
            ArticleID: story.id,
            Category: get(sections, [0, "name"]),
            SubCategory: get(sections, [1, "name"]),
            Author: get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name,
            ArticleType: story.access === "subscription" ? "Paid" : "Free",
            tags: story.tags.map(tag => tag.name).join(","),
            Variant: cookies["story_c"] ? cookies["story_c"] : ""
          });
        });
      }

      if (section) {
        let sections = [section];
        const parentSection = getParentSection(section);
        if (parentSection) {
          sections.unshift(parentSection);
        }

        window.webengage &&
          window.webengage.track("Category View", {
            Category: get(sections, [0, "name"])
          });
      }
    }
  }
};

export const Lotame = {
  scriptLoaded: false,

  loadScript(onload) {
    let script;
    if (!this.scriptLoaded) {
      script = document.createElement("script");
      script.type = "text/javascript";
      script.defer = true;
      script.id = "LOTCC_9593";
      script.onload = onload;
      script.src = "https://tags.crwdcntrl.net/c/9593/cc.js?ns=_cc9593";
      const node = document.getElementsByTagName("script")[0];
      node.parentNode.insertBefore(script, node);
      this.scriptLoaded = true;
    } else {
      onload();
    }
  },

  registerPageView({ story, sections }) {
    try {
      sections = story ? story.sections : sections;
      const websiteUrl = get(global.qtConfig, ["sketches-host"]) || "";
      if (sections && sections.length > 0) {
        const childSection = sections[sections.length - 1];
        this.loadScript(() => {
          if (!global._cc9593) {
            return;
          }
          global._cc9593.add("int", `site:${websiteUrl.replace(/^https?:\/\/www\./, "")}`);
          global._cc9593.add("int", "Web");
          global._cc9593.add("int", `${get(childSection, [0, "name"], "")}`);
          global.userPromise.then(user => {
            global._cc9593.add("int", "Registered");
            global._cc9593.add("int", user.userType);
          });
          global._cc9593.bcp();
        });
      }
    } catch (e) {
      console.error(e);
    }
  }
};

export const LoadVuukle = {
  scriptLoaded: false,
  userRegistered: false,
  registerUser() {
    if (this.userRegistered) {
      return;
    }
    global.userPromise
      .then(user => {
        const { username, userEmail } = user;
        this.userRegistered = true;
        isNaN(userEmail) &&
          wretch(`/v/api/vuukle/${userEmail}`)
            .get()
            .text(val => val)
            .then(function(response) {
              const profile = JSON.stringify({
                username: username,
                email: userEmail,
                public_key: get(global.qtConfig, ["vuukle", "public_key"]) || "",
                signature: response
              });
              const signature = window.btoa(profile);
              window.vuukleLogin(signature);
            });
      })
      .catch(() => {
        console.warn("User attribution error occured");
      });
  },
  loadScript(onload, vikatanHosts) {
    let script1, script2;
    if (!this.scriptLoaded) {
      script1 = document.createElement("script");
      script1.type = "text/javascript";
      //  script1.defer = true;
      script1.src = "https://cdn.vuukle.com/platform.js";
      script2 = document.createElement("script");
      script2.innerHTML = `var VUUKLE_CONFIG={host: "vikatan.com","emotes": {
        "firstName": "ஹாப்பி",
        "fourthName": "லவ்",
        "fifthName": "கோபம்",
        "sixthName": "வருத்தம்",
        disable: [2, 3],
        "fourthImg" : "https://${
          global.qtConfig["cdn-image"]
        }/vikatan/2019-11/6d234603-1a98-4ba1-9d75-0a890fd36e42/loveemoji.svg"
    },apiKey:"${get(global.qtConfig, ["vuukle", "public_key"]) || ""}",comments: { transliteration:{
          language: "ta",
          enabledByDefault: true,
          }, auth: { password: false,vuukle: true,sso: { onClick : function() { return window.location.replace("${
            vikatanHosts.login
          }?redirect_url=${global.location.href}") }   } } } ,language:"ta",endlessMode:!0};`;
      const node = document.getElementsByTagName("script")[0];
      node.parentNode.insertBefore(script2, node);
      node.parentNode.insertBefore(script1, node);
      this.scriptLoaded = true;
      onload();
    } else {
      onload();
    }
  },
  register(story, vikatanHosts) {
    const that = this;
    this.loadScript(() => {
      let script;
      script = document.createElement("script");
      let id = story.id;
      // var newStr = id.replace(/-/g, "");
      const vuukleInitializerListenerId = setInterval(function() {
        if (window.VUUKLE_PLATFORM === 1) {
          clearInterval(vuukleInitializerListenerId);
          window.removeVuukleWidgets(id);
          const ele = document.getElementById(`vuukle-widget-${id}`);
          script.innerHTML = `window.newVuukleWidgets({elementsIndex:"${id}",articleId:"${id}",img:"https://${
            global.qtConfig["cdn-image"]
          }/${story["hero-image-s3-key"]}",title:"${story.headline
            .replace(/"/g, '\\"')
            .replace(/(\r\n|\n|\r)/gm, "")}",tags:"",url:"${story.url}"});`;
          if (typeof ele !== "undefined" && ele != null) {
            ele.appendChild(script);
          }
          that.registerUser();
        }
      });
    }, vikatanHosts);
  }
};
